import { Component, type ErrorInfo, type ReactNode } from 'react';
import { t } from '@/shared/i18n';
import { dropOfflineCopy } from '@/shared/offline/dropCopy';
import { ErrorNotice } from '@/shared/ui/ErrorNotice';
import s from './rootError.module.css';
import { shouldSelfHeal } from './selfHeal';

function reopen(): void {
  void dropOfflineCopy().then(() => {
    window.location.reload();
  });
}

interface State {
  readonly error: Error | null;
}

/**
 * Страница не должна становиться белой: любая ошибка отрисовки говорит, что случилось, почему и что делать.
 * Без границы React при ошибке убирает всё приложение, и человек видит пустой экран (iPhone, 30.10).
 */
export class RootErrorBoundary extends Component<
  { readonly children: ReactNode },
  State
> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Chat: ошибка отрисовки', error, info.componentStack);
    if (shouldSelfHeal()) reopen();
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return (
      <div className={s.page}>
        <ErrorNotice
          what={t('Chat не открылся')}
          why={`${t('Приложение споткнулось на ошибке:')} ${error.message}`}
          action={{ label: t('Открыть заново'), onClick: reopen }}
          avoid={t(
            'Кнопка стирает копию данных на этом устройстве и загружает всё с сервера — переписка не пропадёт. Если ошибка повторяется, пришлите этот экран разработчику.',
          )}
        />
      </div>
    );
  }
}
